import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import GenericModal from "src/components/generic-modal";
import { TextField, Grid } from "@mui/material";
import { getAllRoles } from "src/queries/query";
import { createUser } from "src/queries/queriess";
import { toast } from "react-toastify";

export default function CreateDriver({ open, handleClose, onSuccess }) {
  const [roles, setRoles] = useState([]);

  // Fetch roles
  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const rolesResult = await getAllRoles();
        if (rolesResult?.status === 200) {
          setRoles(rolesResult.data.data);
        }
      } catch (error) {
        console.error("Failed to fetch roles:", error);
      }
    };

    fetchRoles();
  }, []);

  const formik = useFormik({
    initialValues: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      password: "",
      confirmPassword: "",
    },
    validationSchema: Yup.object({
      firstName: Yup.string().required("Required"),
      lastName: Yup.string().required("Required"),
      email: Yup.string().email("Invalid email address").required("Required"),
      phone: Yup.string().required("Required"),
      password: Yup.string()
        .min(6, "Password must be at least 6 characters")
        .required("Required"),
      confirmPassword: Yup.string()
        .oneOf([Yup.ref("password")], "Passwords must match")
        .required("Required"),
    }),
    onSubmit: async (values) => {
      try {
        const driverRole = roles.find(
          (role) => role.name.toLowerCase() === "driver"
        );

        if (!driverRole) {
          toast.error("Driver role not found. Please contact administrator.");
          return;
        }

        const payload = {
          firstName: values.firstName,
          lastName: values.lastName,
          email: values.email,
          phone: values.phone,
          password: values.password,
          roleUuid: driverRole.uuid,
          status: true,
          driverType: "company driver",
          mobile: false,
          tagsUuids: [],
        };

        const result = await createUser(payload);

        if (result?.status === 200) {
          toast.success("Driver created successfully.");
          formik.resetForm();
          handleClose();
          onSuccess?.();
        } else {
          toast.error(result?.data?.message || "Something went wrong.");
        }
      } catch (error) {
        console.error("Error during submission:", error);
        toast.error(error?.message || "Failed to create driver.");
      }
    },
  });

  return (
    <GenericModal
      open={open}
      onClose={handleClose}
      title="Create new driver"
      primaryButtonText="Add"
      secondaryButtonText="Cancel"
      onPrimaryAction={formik.handleSubmit}
      maxWidth="sm"
      fullWidth
      desktopWidth="800px"
    >
      <form onSubmit={formik.handleSubmit}>
        <Grid container spacing={2}>
          <Grid item sm={6}>
            <TextField
              fullWidth
              name="firstName"
              label="First Name"
              value={formik.values.firstName}
              onChange={formik.handleChange}
              error={formik.touched.firstName && Boolean(formik.errors.firstName)}
              helperText={formik.touched.firstName && formik.errors.firstName}
            />
          </Grid>

          <Grid item sm={6}>
            <TextField
              fullWidth
              name="lastName"
              label="Last Name"
              value={formik.values.lastName}
              onChange={formik.handleChange}
              error={formik.touched.lastName && Boolean(formik.errors.lastName)}
              helperText={formik.touched.lastName && formik.errors.lastName}
            />
          </Grid>

          <Grid item sm={6}>
            <TextField
              fullWidth
              name="email"
              label="Email"
              type="email"
              value={formik.values.email}
              onChange={formik.handleChange}
              error={formik.touched.email && Boolean(formik.errors.email)}
              helperText={formik.touched.email && formik.errors.email}
            />
          </Grid>

          <Grid item sm={6}>
            <TextField
              fullWidth
              name="phone"
              label="Phone Number"
              value={formik.values.phone}
              onChange={formik.handleChange}
              error={formik.touched.phone && Boolean(formik.errors.phone)}
              helperText={formik.touched.phone && formik.errors.phone}
            />
          </Grid>

          <Grid item sm={6}>
            <TextField
              fullWidth
              name="password"
              label="Password"
              type="password"
              value={formik.values.password}
              onChange={formik.handleChange}
              error={formik.touched.password && Boolean(formik.errors.password)}
              helperText={formik.touched.password && formik.errors.password}
            />
          </Grid>

          <Grid item sm={6}>
            <TextField
              fullWidth
              name="confirmPassword"
              label="Confirm Password"
              type="password"
              value={formik.values.confirmPassword}
              onChange={formik.handleChange}
              error={
                formik.touched.confirmPassword &&
                Boolean(formik.errors.confirmPassword)
              }
              helperText={
                formik.touched.confirmPassword &&
                formik.errors.confirmPassword
              }
            />
          </Grid>
        </Grid>
      </form>
    </GenericModal>
  );
}
